import React, {useEffect, useState} from 'react';
import {get, push, ref, remove, set, update} from 'firebase/database';
import {database} from '../../services/firebase';
import {useAuth} from '../../contexts/AuthContext';
import Header from '../../components/common/Header';

export default function AdminQuestions() {
    const {userRole} = useAuth();
    const [questions, setQuestions] = useState([]);
    const [filteredQuestions, setFilteredQuestions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    // États pour les filtres
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [difficultyFilter, setDifficultyFilter] = useState('all');
    const [bookFilter, setBookFilter] = useState('all');
    const [testamentFilter, setTestamentFilter] = useState('all');

    // États pour le formulaire
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [formMode, setFormMode] = useState('create'); // 'create' ou 'edit'
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [formData, setFormData] = useState({
        text: '',
        options: ['', '', '', ''],
        correctAnswer: 0,
        verse: '',
        explanation: '',
        category: '',
        bookReference: '',
        testament: 'old',
        difficulty: 'medium',
        tags: []
    });

    // États pour les menus déroulants
    const [categories, setCategories] = useState([]);
    const [books, setBooks] = useState([]);
    const [tags, setTags] = useState([]);

    // Récupération des questions depuis Firebase
    useEffect(() => {
        async function fetchData() {
            try {
                if (userRole !== 'admin') {
                    setError('Accès non autorisé. Vous devez être administrateur pour voir cette page.');
                    setIsLoading(false);
                    return;
                }

                // Récupérer les questions
                const questionsRef = ref(database, 'questionBank');
                const snapshot = await get(questionsRef);

                if (!snapshot.exists()) {
                    setIsLoading(false);
                    return;
                }

                const questionsData = snapshot.val();
                const processedQuestions = Object.entries(questionsData).map(([id, data]) => ({
                    id,
                    ...data
                }));

                // Trier par date de création (plus récent en premier)
                processedQuestions.sort((a, b) => {
                    const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
                    const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
                    return dateB - dateA;
                });

                setQuestions(processedQuestions);
                setFilteredQuestions(processedQuestions);

                // Extraire les catégories, livres et tags uniques
                const uniqueCategories = new Set();
                const uniqueBooks = new Set();
                const uniqueTags = new Set();

                processedQuestions.forEach(question => {
                    if (question.category) uniqueCategories.add(question.category);
                    if (question.bookReference) uniqueBooks.add(question.bookReference);
                    if (Array.isArray(question.tags)) {
                        question.tags.forEach(tag => uniqueTags.add(tag));
                    }
                });

                setCategories(Array.from(uniqueCategories).sort());
                setBooks(Array.from(uniqueBooks).sort());
                setTags(Array.from(uniqueTags).sort());

            } catch (error) {
                console.error("Erreur lors de la récupération des questions:", error);
                setError("Une erreur s'est produite lors du chargement des questions.");
            } finally {
                setIsLoading(false);
            }
        }

        fetchData();
    }, [userRole]);

    // Appliquer les filtres
    useEffect(() => {
        let result = [...questions];

        // Appliquer la recherche
        if (searchQuery) {
            result = result.filter(
                question =>
                    question.text?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    question.explanation?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    question.verse?.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        // Appliquer le filtre de catégorie
        if (categoryFilter !== 'all') {
            result = result.filter(question => question.category === categoryFilter);
        }

        // Appliquer le filtre de difficulté
        if (difficultyFilter !== 'all') {
            result = result.filter(question => question.difficulty === difficultyFilter);
        }

        // Appliquer le filtre de livre
        if (bookFilter !== 'all') {
            result = result.filter(question => question.bookReference === bookFilter);
        }

        // Appliquer le filtre de testament
        if (testamentFilter !== 'all') {
            result = result.filter(question => question.testament === testamentFilter);
        }

        setFilteredQuestions(result);
    }, [questions, searchQuery, categoryFilter, difficultyFilter, bookFilter, testamentFilter]);

    // Gérer le changement dans le formulaire
    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setFormData(prev => ({...prev, [name]: value}));
    };

    // Gérer le changement des options de réponse
    const handleOptionChange = (index, value) => {
        const newOptions = [...formData.options];
        newOptions[index] = value;
        setFormData(prev => ({...prev, options: newOptions}));
    };

    // Gérer le changement des tags (séparés par des virgules)
    const handleTagsChange = (e) => {
        const tagsString = e.target.value;
        const tagsArray = tagsString.split(',').map(tag => tag.trim()).filter(tag => tag);
        setFormData(prev => ({...prev, tags: tagsArray}));
    };

    // Ouvrir le formulaire en mode création
    const openCreateForm = () => {
        setFormData({
            text: '',
            options: ['', '', '', ''],
            correctAnswer: 0,
            verse: '',
            explanation: '',
            category: '',
            bookReference: '',
            testament: 'old',
            difficulty: 'medium',
            tags: []
        });
        setFormMode('create');
        setIsFormOpen(true);
    };

    // Ouvrir le formulaire en mode édition
    const openEditForm = (question) => {
        setCurrentQuestion(question);
        setFormData({
            text: question.text || '',
            options: question.options || ['', '', '', ''],
            correctAnswer: question.correctAnswer || 0,
            verse: question.verse || '',
            explanation: question.explanation || '',
            category: question.category || '',
            bookReference: question.bookReference || '',
            testament: question.testament || 'old',
            difficulty: question.difficulty || 'medium',
            tags: question.tags || []
        });
        setFormMode('edit');
        setIsFormOpen(true);
    };

    // Créer une nouvelle question
    const createQuestion = async () => {
        try {
            const newQuestion = {
                ...formData,
                createdAt: new Date().toISOString(),
                createdBy: "admin", // Remplacer par l'ID de l'admin authentifié
                sourceQuizId: null
            };

            const questionsRef = ref(database, 'questionBank');
            const newQuestionRef = push(questionsRef);
            await set(newQuestionRef, newQuestion);

            // Ajouter la nouvelle question à la liste locale
            setQuestions(prev => [{
                id: newQuestionRef.key,
                ...newQuestion
            }, ...prev]);

            setIsFormOpen(false);
            alert("Question créée avec succès!");
        } catch (error) {
            console.error("Erreur lors de la création de la question:", error);
            alert(`Erreur: ${error.message}`);
        }
    };

    // Mettre à jour une question existante
    const updateQuestion = async () => {
        if (!currentQuestion) return;

        try {
            const questionRef = ref(database, `questionBank/${currentQuestion.id}`);

            const updatedQuestion = {
                ...formData,
                updatedAt: new Date().toISOString()
            };

            await update(questionRef, updatedQuestion);

            // Mettre à jour la question dans la liste locale
            setQuestions(prev =>
                prev.map(q =>
                    q.id === currentQuestion.id ? {...q, ...updatedQuestion, updatedAt: new Date().toISOString()} : q
                )
            );

            setIsFormOpen(false);
            alert("Question mise à jour avec succès!");
        } catch (error) {
            console.error("Erreur lors de la mise à jour de la question:", error);
            alert(`Erreur: ${error.message}`);
        }
    };

    // Confirmer la suppression d'une question
    const confirmDelete = (question) => {
        setCurrentQuestion(question);
        setIsDeleteDialogOpen(true);
    };

    // Supprimer une question
    const deleteQuestion = async () => {
        if (!currentQuestion) return;

        try {
            const questionRef = ref(database, `questionBank/${currentQuestion.id}`);
            await remove(questionRef);

            // Supprimer la question de la liste locale
            setQuestions(prev => prev.filter(q => q.id !== currentQuestion.id));

            setIsDeleteDialogOpen(false);
            alert("Question supprimée avec succès!");
        } catch (error) {
            console.error("Erreur lors de la suppression de la question:", error);
            alert(`Erreur: ${error.message}`);
        }
    };

    // Soumission du formulaire
    const handleSubmit = (e) => {
        e.preventDefault();

        // Validation de base
        if (!formData.text || formData.options.some(option => !option) || !formData.explanation) {
            alert("Veuillez remplir tous les champs obligatoires (texte, options, explication).");
            return;
        }

        if (formMode === 'create') {
            createQuestion();
        } else {
            updateQuestion();
        }
    };

    // Formater la réponse pour l'affichage
    const formatCorrectAnswer = (question) => {
        if (!question.options || !Array.isArray(question.options)) return "N/A";

        const index = question.correctAnswer;
        return question.options[index] || "N/A";
    };

    // Réinitialiser les filtres
    const resetFilters = () => {
        setSearchQuery('');
        setCategoryFilter('all');
        setDifficultyFilter('all');
        setBookFilter('all');
        setTestamentFilter('all');
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-indigo-100">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-indigo-100">
                <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
                    <div className="text-center">
                        <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24"
                             stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                        </svg>
                        <h3 className="mt-2 text-lg font-medium text-gray-900">Erreur</h3>
                        <p className="mt-1 text-sm text-gray-500">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100">
            <Header title="Gestion des Questions"/>

            <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Gestion des Questions</h1>
                        <p className="text-gray-500">Ajoutez, modifiez ou supprimez des questions pour les quiz</p>
                    </div>

                    <button
                        type="button"
                        onClick={openCreateForm}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
                        </svg>
                        Ajouter une question
                    </button>
                </div>

                {/* Filtres */}
                <div className="bg-white shadow rounded-lg mb-6">
                    <div className="px-4 py-5 sm:p-6">
                        <h2 className="text-lg font-medium text-gray-900 mb-4">Filtrer les questions</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                            <div>
                                <label htmlFor="search"
                                       className="block text-sm font-medium text-gray-700">Recherche</label>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <div
                                        className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24"
                                             stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                                        </svg>
                                    </div>
                                    <input
                                        type="text"
                                        name="search"
                                        id="search"
                                        className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-3 py-2 border-gray-300 rounded-md"
                                        placeholder="Texte, verset, explication..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="category"
                                       className="block text-sm font-medium text-gray-700">Catégorie</label>
                                <select
                                    id="category"
                                    name="category"
                                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                    value={categoryFilter}
                                    onChange={(e) => setCategoryFilter(e.target.value)}
                                >
                                    <option value="all">Toutes les catégories</option>
                                    {categories.map(category => (
                                        <option key={category} value={category}>{category}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label htmlFor="difficulty"
                                       className="block text-sm font-medium text-gray-700">Difficulté</label>
                                <select
                                    id="difficulty"
                                    name="difficulty"
                                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                    value={difficultyFilter}
                                    onChange={(e) => setDifficultyFilter(e.target.value)}
                                >
                                    <option value="all">Toutes les difficultés</option>
                                    <option value="easy">Facile</option>
                                    <option value="medium">Moyen</option>
                                    <option value="hard">Difficile</option>
                                </select>
                            </div>

                            <div>
                                <label htmlFor="book" className="block text-sm font-medium text-gray-700">Livre</label>
                                <select
                                    id="book"
                                    name="book"
                                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                    value={bookFilter}
                                    onChange={(e) => setBookFilter(e.target.value)}
                                >
                                    <option value="all">Tous les livres</option>
                                    {books.map(book => (
                                        <option key={book} value={book}>{book}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label htmlFor="testament"
                                       className="block text-sm font-medium text-gray-700">Testament</label>
                                <select
                                    id="testament"
                                    name="testament"
                                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                    value={testamentFilter}
                                    onChange={(e) => setTestamentFilter(e.target.value)}
                                >
                                    <option value="all">Tous les testaments</option>
                                    <option value="old">Ancien Testament</option>
                                    <option value="new">Nouveau Testament</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 flex justify-between items-center">
                        <div className="text-sm text-gray-500">
                            {filteredQuestions.length} question(s) trouvée(s)
                        </div>
                        <button
                            type="button"
                            onClick={resetFilters}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            Réinitialiser les filtres
                        </button>
                    </div>
                </div>

                {/* Liste des questions */}
                <div className="bg-white shadow rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                            <tr>
                                <th scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Question
                                </th>
                                <th scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Réponse correcte
                                </th>
                                <th scope="col"
                                    className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Difficulté
                                </th>
                                <th scope="col"
                                    className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Catégorie
                                </th>
                                <th scope="col"
                                    className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Livre
                                </th>
                                <th scope="col"
                                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                            {filteredQuestions.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                                        Aucune question trouvée
                                    </td>
                                </tr>
                            ) : (
                                filteredQuestions.map((question) => (
                                    <tr key={question.id}>
                                        <td className="px-6 py-4 text-sm text-gray-900">
                                            <div className="text-sm font-medium text-gray-900">{question.text}</div>
                                            {question.verse && (
                                                <div className="text-xs text-gray-500 mt-1">
                                                    Verset: {question.verse}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500">
                                            {formatCorrectAnswer(question)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                                <span
                                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                        question.difficulty === 'easy'
                                                            ? 'bg-green-100 text-green-800'
                                                            : question.difficulty === 'medium'
                                                                ? 'bg-yellow-100 text-yellow-800'
                                                                : 'bg-red-100 text-red-800'
                                                    }`}>
                                                    {question.difficulty === 'easy' ? 'Facile' :
                                                        question.difficulty === 'medium' ? 'Moyen' : 'Difficile'}
                                                </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                                            {question.category || '-'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                                            {question.bookReference || '-'}
                                            {question.testament && (
                                                <div className="text-xs text-gray-500 mt-1">
                                                    {question.testament === 'old' ? 'Ancien Testament' : 'Nouveau Testament'}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex justify-end space-x-2">
                                                <button
                                                    onClick={() => openEditForm(question)}
                                                    className="text-indigo-600 hover:text-indigo-900"
                                                >
                                                    Éditer
                                                </button>
                                                <button
                                                    onClick={() => confirmDelete(question)}
                                                    className="text-red-600 hover:text-red-900"
                                                >
                                                    Supprimer
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>

            {/* Modal de création/édition de question */}
            {isFormOpen && (
                <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog"
                     aria-modal="true">
                    <div
                        className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                             aria-hidden="true"></div>

                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen"
                              aria-hidden="true">&#8203;</span>

                        <div
                            className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
                            <form onSubmit={handleSubmit}>
                                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                            <h3 className="text-lg leading-6 font-medium text-gray-900"
                                                id="modal-title">
                                                {formMode === 'create' ? 'Ajouter une nouvelle question' : 'Modifier la question'}
                                            </h3>
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500">
                                                    Remplissez tous les champs obligatoires
                                                    pour {formMode === 'create' ? 'créer' : 'mettre à jour'} la
                                                    question.
                                                </p>
                                            </div>

                                            <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                                <div className="sm:col-span-6">
                                                    <label htmlFor="text"
                                                           className="block text-sm font-medium text-gray-700">
                                                        Texte de la question <span className="text-red-500">*</span>
                                                    </label>
                                                    <div className="mt-1">
                                                        <textarea
                                                            id="text"
                                                            name="text"
                                                            rows={3}
                                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                            placeholder="Entrez le texte de la question..."
                                                            value={formData.text}
                                                            onChange={handleInputChange}
                                                            required
                                                        />
                                                    </div>
                                                </div>

                                                <div className="sm:col-span-6">
                                                    <label className="block text-sm font-medium text-gray-700">
                                                        Options de réponse <span className="text-red-500">*</span>
                                                    </label>
                                                    <div className="mt-1 space-y-3">
                                                        {formData.options.map((option, index) => (
                                                            <div key={index} className="flex gap-3 items-center">
                                                                <input
                                                                    type="text"
                                                                    placeholder={`Option ${index + 1}`}
                                                                    value={option}
                                                                    onChange={(e) => handleOptionChange(index, e.target.value)}
                                                                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                                    required
                                                                />
                                                                <div className="flex items-center gap-2">
                                                                    <input
                                                                        type="radio"
                                                                        id={`correctAnswer-${index}`}
                                                                        name="correctAnswer"
                                                                        checked={formData.correctAnswer === index}
                                                                        onChange={() => setFormData({
                                                                            ...formData,
                                                                            correctAnswer: index
                                                                        })}
                                                                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                                                                    />
                                                                    <label htmlFor={`correctAnswer-${index}`}
                                                                           className="text-sm text-gray-700">
                                                                        Correcte
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div className="sm:col-span-3">
                                                    <label htmlFor="verse"
                                                           className="block text-sm font-medium text-gray-700">
                                                        Verset
                                                    </label>
                                                    <div className="mt-1">
                                                        <input
                                                            type="text"
                                                            name="verse"
                                                            id="verse"
                                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                            placeholder="Ex: Jean 3:16"
                                                            value={formData.verse}
                                                            onChange={handleInputChange}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="sm:col-span-3">
                                                    <label htmlFor="category"
                                                           className="block text-sm font-medium text-gray-700">
                                                        Catégorie <span className="text-red-500">*</span>
                                                    </label>
                                                    <div className="mt-1">
                                                        <input
                                                            type="text"
                                                            name="category"
                                                            id="category"
                                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                            placeholder="Ex: Nouveau Testament"
                                                            value={formData.category}
                                                            onChange={handleInputChange}
                                                            list="category-list"
                                                            required
                                                        />
                                                        <datalist id="category-list">
                                                            {categories.map(category => (
                                                                <option key={category} value={category}/>
                                                            ))}
                                                        </datalist>
                                                    </div>
                                                </div>

                                                <div className="sm:col-span-3">
                                                    <label htmlFor="bookReference"
                                                           className="block text-sm font-medium text-gray-700">
                                                        Livre biblique
                                                    </label>
                                                    <div className="mt-1">
                                                        <input
                                                            type="text"
                                                            name="bookReference"
                                                            id="bookReference"
                                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                            placeholder="Ex: Genèse"
                                                            value={formData.bookReference}
                                                            onChange={handleInputChange}
                                                            list="books-list"
                                                        />
                                                        <datalist id="books-list">
                                                            {books.map(book => (
                                                                <option key={book} value={book}/>
                                                            ))}
                                                        </datalist>
                                                    </div>
                                                </div>

                                                <div className="sm:col-span-3">
                                                    <label htmlFor="testament"
                                                           className="block text-sm font-medium text-gray-700">
                                                        Testament
                                                    </label>
                                                    <div className="mt-1">
                                                        <select
                                                            id="testament"
                                                            name="testament"
                                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                            value={formData.testament}
                                                            onChange={handleInputChange}
                                                        >
                                                            <option value="old">Ancien Testament</option>
                                                            <option value="new">Nouveau Testament</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className="sm:col-span-3">
                                                    <label htmlFor="difficulty"
                                                           className="block text-sm font-medium text-gray-700">
                                                        Difficulté <span className="text-red-500">*</span>
                                                    </label>
                                                    <div className="mt-1">
                                                        <select
                                                            id="difficulty"
                                                            name="difficulty"
                                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                            value={formData.difficulty}
                                                            onChange={handleInputChange}
                                                            required
                                                        >
                                                            <option value="easy">Facile</option>
                                                            <option value="medium">Moyen</option>
                                                            <option value="hard">Difficile</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className="sm:col-span-3">
                                                    <label htmlFor="tags"
                                                           className="block text-sm font-medium text-gray-700">
                                                        Tags (séparés par des virgules)
                                                    </label>
                                                    <div className="mt-1">
                                                        <input
                                                            type="text"
                                                            name="tags"
                                                            id="tags"
                                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                            placeholder="Ex: personnages, roi, David"
                                                            value={formData.tags.join(', ')}
                                                            onChange={handleTagsChange}
                                                            list="tags-list"
                                                        />
                                                        <datalist id="tags-list">
                                                            {tags.map(tag => (
                                                                <option key={tag} value={tag}/>
                                                            ))}
                                                        </datalist>
                                                    </div>
                                                </div>

                                                <div className="sm:col-span-6">
                                                    <label htmlFor="explanation"
                                                           className="block text-sm font-medium text-gray-700">
                                                        Explication <span className="text-red-500">*</span>
                                                    </label>
                                                    <div className="mt-1">
                                                        <textarea
                                                            id="explanation"
                                                            name="explanation"
                                                            rows={3}
                                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                            placeholder="Explication de la réponse correcte..."
                                                            value={formData.explanation}
                                                            onChange={handleInputChange}
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                    <button
                                        type="submit"
                                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                                    >
                                        {formMode === 'create' ? 'Créer la question' : 'Mettre à jour'}
                                    </button>
                                    <button
                                        type="button"
                                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                        onClick={() => setIsFormOpen(false)}
                                    >
                                        Annuler
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Dialogue de confirmation de suppression */}
            {isDeleteDialogOpen && (
                <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog"
                     aria-modal="true">
                    <div
                        className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                             aria-hidden="true"></div>

                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen"
                              aria-hidden="true">&#8203;</span>

                        <div
                            className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div
                                        className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                        <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24"
                                             stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                                        </svg>
                                    </div>
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                        <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                            Confirmer la suppression
                                        </h3>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500">
                                                Êtes-vous sûr de vouloir supprimer cette question ? Cette action est
                                                irréversible.
                                            </p>
                                            {currentQuestion && (
                                                <p className="mt-2 text-sm font-medium text-gray-900">
                                                    "{currentQuestion.text}"
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button
                                    type="button"
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={deleteQuestion}
                                >
                                    Supprimer
                                </button>
                                <button
                                    type="button"
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={() => setIsDeleteDialogOpen(false)}
                                >
                                    Annuler
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
